import React, { useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import TimelineIcon from "@mui/icons-material/Timeline";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { getRightworkAPI } from "../../requests/utilities/requests";
import { API_get_plan_task_comments, API_get_plan_task_timeline } from "../../../../constants";
import { TimelineList } from "./TaskTimeline";
import { CommentList } from "./TaskConversation";

// The two hover icons on a task row, ported from the ticket list
// (MaintenanceHistoryTooltip / MaintenanceCommentTooltip): hover shows the
// latest entries, click opens the surface on that tab. Both fetch on first
// hover and forget what they loaded when the row's freshness key changes.

const TOOLTIP_SX = {
  tooltip: {
    sx: {
      maxHeight: "500px",
      overflow: "auto",
      backgroundColor: "white",
      padding: "20px",
      borderRadius: "8px",
      boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
      border: "1px solid #e2e8f0",
      color: "inherit",
      width: "350px",
    },
  },
};

export function TaskHistoryTooltip({ storeId, occurrenceUuid, occurrenceDate, tz, onClick }) {
  const [timeline, setTimeline] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetched on every open: a pin or a saved step changes the history without
  // changing anything the row shows, so there is no key to cache on.
  const fetchTimeline = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const res = await getRightworkAPI(API_get_plan_task_timeline, {
        store_id: storeId,
        plan_task_assignment_uuid: occurrenceUuid,
        specific_date: occurrenceDate,
      });
      setTimeline(res.data?.redux?.payload?.plan_task_timeline || []);
    } catch (error) {
      // Leave null so the next hover retries.
    } finally {
      setIsLoading(false);
    }
  };

  const content = (
    <div>
      <h4 className="text-slate-700 text-sm font-bold mb-3">TASK HISTORY</h4>
      {isLoading || timeline === null ? (
        <div className="flex items-center justify-center py-4">
          <CircularProgress size={20} sx={{ color: "#6d28d9" }} />
        </div>
      ) : (
        <TimelineList timeline={timeline} loading={false} tz={tz} compact />
      )}
    </div>
  );

  return (
    <Tooltip title={content} onOpen={fetchTimeline} componentsProps={TOOLTIP_SX}>
      <span
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        className="inline-flex"
      >
        <TimelineIcon
          className="text-slate-400 hover:text-slate-600 cursor-pointer transition-colors"
          style={{ fontSize: "16px" }}
        />
      </span>
    </Tooltip>
  );
}

export function TaskCommentTooltip({ storeId, occurrenceUuid, occurrenceDate, commentCount, onClick }) {
  const [comments, setComments] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    setComments(null);
  }, [commentCount, occurrenceUuid]);

  const fetchComments = async () => {
    if (comments !== null || isLoading) return;
    setIsLoading(true);
    try {
      const res = await getRightworkAPI(API_get_plan_task_comments, {
        store_id: storeId,
        plan_task_assignment_uuid: occurrenceUuid,
        specific_date: occurrenceDate,
      });
      setComments(res.data?.redux?.payload?.plan_task_comments || []);
    } catch (error) {
      // Leave null so the next hover retries.
    } finally {
      setIsLoading(false);
    }
  };

  if (!commentCount) return null;

  const content = (
    <div>
      <h4 className="text-slate-700 text-sm font-bold mb-3">COMMENTS</h4>
      {isLoading || comments === null ? (
        <div className="flex items-center justify-center py-4">
          <CircularProgress size={20} sx={{ color: "#6d28d9" }} />
        </div>
      ) : (
        <CommentList comments={comments} loading={false} dense emptyHint="" />
      )}
    </div>
  );

  return (
    <Tooltip title={content} onOpen={fetchComments} componentsProps={TOOLTIP_SX}>
      <span
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        className="inline-flex items-center gap-0.5 cursor-pointer text-slate-400 hover:text-violet-700 transition-colors"
      >
        <ChatOutlinedIcon style={{ fontSize: "16px" }} />
        <span className="text-[11px] font-semibold tabular-nums">{commentCount}</span>
      </span>
    </Tooltip>
  );
}
